import React, { useEffect, useRef } from 'react';
import { OrderType } from '../../utils/utils';
import { getDaysUntilPickupAt } from '../../utils/getTransitCounterLevel';
import { getPlanningNextTierLabel, getPlanningRequestCount } from '../../utils/planningTier';
import { canViewTransportPrices } from '../../helpers/permissions';

// Human label for the pre-advice transit horizon (days until pickup at the
// warehouse). Mirrors the day-aware marker (D=dziś, J=jutro) shown on the map.
const transitLabel = (days) => {
  if (days === null || days < 0) return null;
  if (days === 0) return 'dziś';
  if (days === 1) return 'jutro';
  return `za ${days} dni`;
};

const PLANNING_TIER_LABELS = {
  0: 'Tier 0 — jest zapas',
  1: 'Tier 1 — jutro ostatni dzień',
  2: 'Tier 2 — zaplanować dziś',
  3: 'Tier 3 — po terminie',
};

export const InfoWindow = ({ order, onClose, planningMode = false, hasReturnPicking = false }) => {
    const isReturnPickingOrder = hasReturnPicking && Boolean(order?.isReturnPicking);
    const hasPlanning = planningMode
      && order?.warehouseTier !== undefined
      && order?.warehouseTier !== null;
    const nextTierLabel = getPlanningNextTierLabel(order);
    const prepareComments = () => {
      return order.orderComments.map((comment) => {
        return `${comment.user?.firstName} ${comment.user?.lastName} - ${comment.message}`;
      });
    }

    const ref = useRef(null);

  function isNotCompletedIncomePrice() {
    // Optional chaining mirrors TimeLineItem: incomePrice is absent once the
    // payload has been sanitized for a user without price permission.
    return order.incomePrice?.notPricedPackages != null && order.incomePrice.notPricedPackages.length > 0;
  }

    useEffect(() => {
      const el = ref.current;
      if (!el) return;

      const stopForMap = (e) => {
        if (e.target.closest('.info-window__close')) {
          return;
        }

        e.stopPropagation();
        e.nativeEvent?.stopImmediatePropagation?.();
      };

      const stopWheel = (e) => {
        e.stopPropagation();
      };

      el.addEventListener('mousedown', stopForMap, true);
      el.addEventListener('click', stopForMap, true);
      el.addEventListener('wheel', stopWheel, { passive: false, capture: true });

      return () => {
        el.removeEventListener('mousedown', stopForMap, true);
        el.removeEventListener('click', stopForMap, true);
        el.removeEventListener('wheel', stopWheel, true);
      };
    }, []);

    return (
        <div
          ref={ref}
          className="info-window"
        >
          <div className="info-window__header">
          <div className="info-window__close" onClick={onClose}>
            <i className="bx bx-x-circle"></i>
          </div>

          <p className="text-black tx-12 info-window__title">{order?.parcelDescription}</p>
          </div>

          <div className="info-window__body">

          <div className="text-bold-700 text-black tx-08">
              <a href={`/${window.urlPrefix}/orders/show/${order?.id}`} target="_blank">
                Nr zam.: {order?.trackingId || '–'}
              </a>

              {isReturnPickingOrder && (
                <span className="badge badge-black text-bold-600 ml-1">
                  { __('routes.map_info.return') }
                </span>
              )}
          </div>

          <div className="text-bold-700 text-black tx-08 mb-1">
              Nr klienta: {order?.senderOrderId || '–'}
          </div>

            <div className="text-bold-700 text-black tx-08">
                Producent: {order?.manufacturerRelation?.name || order?.manufacturer || '–'}
            </div>

            <div className="text-bold-700 text-black tx-08">
                Sprzedawca: {order?.sender?.shortName}
            </div>

            <div className="text-bold-700 text-black tx-08 mb-1">
              Mebel: {order?.productNames.join(', ')}
            </div>

            {canViewTransportPrices() && !hasReturnPicking && (
              <div data-testid="info-window-income">
                <div className="text-bold-700 text-black tx-08">
                  {__('info_window.income.total', {value: order.incomePrice.price.amount, currency: order.incomePrice.price.currency})}

                  {isNotCompletedIncomePrice() && (
                    <>
                      <span className="text-danger font-weight-bold">&nbsp;!!!</span>
                    </>
                  )}
                </div>

                <div className="text-bold-700 text-black tx-08">
                  {__('info_window.income.order_income', {value: order.incomePrice.delivery.amount, currency: order.incomePrice.delivery.currency})}
                </div>

                <div className="text-bold-700 text-black tx-08">
                  {__('info_window.income.exchange_income', {value: order.incomePrice.return.amount, currency: order.incomePrice.return.currency})}
                </div>
              </div>
            )}

            <div className="text-bold-700 text-black tx-08 mb-1">
              {__('info_window.volume', {volume: order.packagesVolumeM3})}
            </div>

            <div>
              {isReturnPickingOrder
                ? order?.returnWarehouse?.name
                : <>{order?.deliveryFirstName} {order?.deliveryLastName}</>
              }
            </div>

            <div>
              {isReturnPickingOrder
                ? <>{order?.returnWarehouse?.postcode} {order?.returnWarehouse?.city}</>
                : <>{order?.deliveryPostcode} {order?.deliveryCity}</>
              }
            </div>

            <div>
              {isReturnPickingOrder
                ? <>{order?.returnWarehouse?.street} {order?.returnWarehouse?.house}</>
                : <>{order?.deliveryStreet} {order?.deliveryHouse}</>
              }
            </div>

            {order.isUrgent && (
              <>
                  <span className="badge badge-pill badge-light-danger font-small-2 mt-5px">
                      { __('orders.is_urgent') }
                  </span>
              </>
            )}

            {order.isEceUrgent && (
              <>
                  <span className="badge badge-pill badge-warning font-small-2 mt-5px ml-2px">
                      { __('orders.is_ece_urgent') }
                  </span>
              </>
            )}

            <p className="text-danger tx-08 mb-1px">{order?.note}</p>
            <p className="text-warning tx-08 mb-1px">{order?.internalNote}</p>
            {/* Legacy DELIVERY countdown (calendar days until pickup + shipping_days).
                Renamed to make clear it is the delivery horizon, distinct from the
                planning deadline below. Kept visible in all modes. */}
            <p className="text-bold-700 tx-08 mb-1px">Dni do dostawy: {order?.daysLeft}</p>

            {/* Pre-advice transit day (B3): when the order is in transit to the
                warehouse, spell out whether it arrives today / tomorrow / in N
                days, matching the day-aware counter marker on the map. */}
            {(() => {
              const transit = transitLabel(getDaysUntilPickupAt(order));
              return transit ? (
                <p className="text-bold-700 tx-08 mb-1px" data-testid="info-window-transit">
                  Tranzyt: {transit}
                </p>
              ) : null;
            })()}

            {order.orderServices?.length > 0 && (
              <>
                <p className='text-bold-700 tx-08 mb-1px mt-1'>{__('routes.map_info.services')}</p>

                {order.orderServices.map((service) => <div className='mb-1px tx-07'>- {service.service.name}</div>)}
              </>
            ) }

            {order.orderComments?.length > 0 && (
              <>
                <p className="text-bold-700 tx-08 mb-1px">Komentarze</p>
                <div className="info-window__comments">
                  {prepareComments().map((comment, index) => <div key={`${order.id}-comment-${index}`} className="tx-08 mb-5px">{comment}</div>)}
                </div>
              </>
            )}

            {hasPlanning && (
              <div className="info-window__planning mt-1" data-testid="info-window-planning">
                <p className="info-window__planning-title text-bold-700 tx-08 mb-1px">Terminowość planowania</p>
                <p className="text-bold-700 tx-08 mb-1px">Termin planowania: {order?.warehouseDeadline}</p>
                <p className="text-bold-700 tx-08 mb-1px">Do zaplanowania: {order?.warehouseDaysLeft} dni rob.</p>
                <p className="text-bold-700 tx-08 mb-1px">{PLANNING_TIER_LABELS[order?.warehouseTier]}</p>
                {getPlanningRequestCount(order) > 0 && (
                  <p className="text-bold-700 tx-08 mb-1px">Zaplanowano: {getPlanningRequestCount(order)}</p>
                )}
                {order?.warehouseFloorApplied && (
                  <p className="text-warning tx-08 mb-1px">Tier podniesiony przez priorytet</p>
                )}
                {nextTierLabel && (
                  <p className="text-muted tx-07 mb-1px">{nextTierLabel}</p>
                )}
              </div>
            )}
          </div>
        </div>
    )
};
